/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { IRootState } from "../../config/reducers";
import { NotificationAction } from "./ducks/notificationSlice";
import axios from "axios";
import { Ago, AppLogo, CriticalResultTabKey } from "@advapacs/common";
import { notificationAPI } from "../../config/api";
import { readNotification } from "./ducks/operations";
import { useAppDispatch } from "../../config/store";
import { useTranslation } from "react-i18next";
import { useFlag } from "@unleash/proxy-client-react";
import { useNavigate } from "react-router-dom";

const Notifications: FunctionComponent = () => {
  const { t } = useTranslation();
  const enabledCPNotification = useFlag("cp-critical-result-notification");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { entities, pageable, fetching, nextPage } = useSelector(
    (state: IRootState) => state.notificationReducer
  );
  useEffect(() => {
    dispatch(NotificationAction.fetchNotifications(true));
    let url = notificationAPI.GET_NOTIFICATION;
    if (nextPage && nextPage > 0) {
      url = url.concat(`?page=${nextPage}&size=10`);
    }
    axios
      .get(url)
      .then((data) => data)
      .then((notifications) => {
        dispatch(NotificationAction.addNotifications(notifications.data));
        dispatch(NotificationAction.fetchNotifications(false));
      })
      .catch((e) => {
        // handle error
        dispatch(NotificationAction.fetchNotifications(false));
        return e;
      });
  }, [dispatch, nextPage]);
  const bottomBoundaryRef = useRef(null);

  const scrollObserver = useCallback(
    (node) => {
      new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.intersectionRatio > 0 && pageable.hasNext) {
            dispatch(NotificationAction.nextNotificationPage());
          }
        });
      }).observe(node);
    },
    [dispatch, pageable]
  );

  useEffect(() => {
    if (bottomBoundaryRef.current) {
      scrollObserver(bottomBoundaryRef.current);
    }
  }, [scrollObserver, bottomBoundaryRef]);

  const updateStatusNotification = useCallback(
    (id: string, read: boolean) => {
      if (read) return;
      const data = entities
        .filter(
          (value, index, self) =>
            self.findIndex((v) => v.id === value.id) === index
        )
        .map((item) => {
          const tmp = { ...item };
          tmp.read = item.id === id ? true : item.read;
          return tmp;
        });
      dispatch(readNotification(id, data));
    },
    [dispatch, entities]
  );

  const viewDetail = (criticalResultId: string, isReferrer: boolean) => {
    const tabKey =
      isReferrer === true
        ? CriticalResultTabKey.MY_CASE
        : CriticalResultTabKey.COPY_TO_ME;
    navigate("/CriticalResults/" + criticalResultId + "?tabKey=" + tabKey);
  };

  const getIconClass = (type: string, status: string): string => {
    // Access Request : Approved/Denied
    if (type === "access_request") {
      return status === "approved"
        ? "approved_icon fa-solid fa-circle-check"
        : "denied_icon fa-solid fa-circle-xmark";
    }

    // Critical Result
    if (
      type === "critical_result_referrer" ||
      type === "critical_result_copied_to"
    ) {
      return "fa-solid fa-circle-exclamation critical_result_icon";
    }

    // Other cases, no icon
    return "";
  };

  return (
    <div className="">
      <div id="images" className="container">
        <div className="w-100">
          {entities
            .filter(
              (value, index, self) =>
                self.findIndex((v) => v.id === value.id) === index
            )
            .map((notify) => {
              const { id, title, content, read, type, data } = notify;
              const itemClass =
                "align-items-start mb-2 notification-item " +
                (read === true ? "" : "notification-new-item");
              return (
                <>
                  {/*New style of CP Notification item, including Referrer + Result Copied To of Critical Result*/}
                  {enabledCPNotification && (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        updateStatusNotification(id, read);
                      }}
                      className="status-indicator-container me-3"
                    >
                      <div className={itemClass}>
                        <div className="rounded-pill d-flex justify-content-between w-100 left_icon">
                          <span className="flex-1">
                            <span
                              className={getIconClass(type, data?.status)}
                            ></span>
                            <span className="ml-2 font-weight-bold notification-title">
                              {title}
                            </span>
                          </span>
                          {!read && (
                            <span className="bg-notification">
                              <span
                                className="position-absolute start-10 p-1 bg-primary border rounded-circle"
                                style={{ marginLeft: "-10px" }}
                              ></span>
                            </span>
                          )}
                        </div>

                        <div className="flex-fill">
                          <div className="rounded my-2">
                            <span className="form-check ms-1">{content}</span>
                          </div>
                          {(type === "critical_result_referrer" ||
                            type === "critical_result_copied_to") && (
                            <div className="form-check fs-sm mt-1">
                              <a
                                href={"#"}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  viewDetail(
                                    data?.criticalResultId,
                                    data?.status === "critical_result_referrer"
                                  );
                                }}
                                target={"_blank"}
                              >
                                {t("view_details")}
                              </a>
                            </div>
                          )}
                          <div className="form-check fs-sm text-muted mt-1 text-right">
                            <Ago utc={notify.createdAt} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/*Old style of CP Notification item*/}
                  {!enabledCPNotification && (
                    <div className="align-items-start mb-2">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          updateStatusNotification(id, read);
                        }}
                        className="status-indicator-container me-3"
                      >
                        <div className="rounded-pill d-flex justify-content-between w-100">
                          <span className="flex-1">
                            <span>
                              <AppLogo.LogoShort />
                            </span>
                            <span className="ml-2">{title}</span>
                          </span>
                          {!read && (
                            <span className="badge bg-notification text-success">
                              {t("new")}
                            </span>
                          )}
                        </div>
                      </a>
                      <div className="flex-fill">
                        <div className="rounded my-2">
                          <label className="form-check ms-1">{content}</label>
                        </div>
                        <div className="form-check fs-sm text-muted mt-1">
                          <Ago utc={notify.createdAt} />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })}
        </div>
      </div>

      {fetching && (
        <div className="text-center bg-secondary m-auto p-3">
          <p className="m-0 text-white">{t("getting_notification")}</p>
        </div>
      )}
      {pageable.hasNext && (
        <div className="text-center">
          <div
            id="page-bottom-boundary"
            ref={bottomBoundaryRef}
            className="spinner-border"
            role="status"
          ></div>
        </div>
      )}
    </div>
  );
};
export default Notifications;
